import { useEffect, useState, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { PoolCard } from '../components/PoolCard';
import { Pool } from '../types/pool';
import { api } from '../services/api';

interface PoolSectionProps {
  title: string;
  count: number;
  badgeColor: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function PoolSection({ title, count, badgeColor, defaultOpen = true, children }: PoolSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4 w-full text-left group"
      >
        <svg
          className={`w-4 h-4 md:w-5 md:h-5 text-gray-500 transition-transform duration-200 ${open ? 'rotate-0' : '-rotate-90'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        <h2 className="text-lg md:text-2xl font-bold text-gray-900 group-hover:text-gray-700 transition-colors">
          {title}
        </h2>
        <span className={`px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-sm font-medium rounded-full ${badgeColor}`}>
          {count}
        </span>
      </button>
      {open && children}
    </section>
  );
}

export function PoolList() {
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
  const [pools, setPools] = useState<Pool[]>([]);
  const [myPoolIds, setMyPoolIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPools();
  }, []);

  // Load user's entered pool IDs once auth is ready
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadMyPoolIds();
    }
  }, [authLoading, isAuthenticated]);

  const loadPools = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getPools();
      setPools(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pools');
      console.error('Error loading pools:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMyPoolIds = async () => {
    try {
      const token = await getAccessTokenSilently();
      const ids = await api.getMyPoolIds(token);
      setMyPoolIds(new Set(ids));
    } catch (err) {
      console.error('Error loading my pool IDs:', err);
    }
  };

  // "My Pools" intentionally excludes completed/cancelled pools — those crowd
  // the top of the page and add visual noise. Users can still find their past
  // entries in the bottom "Completed" section, where the "Entered" badge
  // makes ownership obvious.
  const myPools = useMemo(
    () =>
      pools.filter(
        (p) =>
          myPoolIds.has(p.id) &&
          p.status !== 'COMPLETED' &&
          p.status !== 'CANCELLED',
      ),
    [pools, myPoolIds],
  );

  const acceptingEntries = useMemo(
    () => pools.filter(p => p.status === 'OPEN'),
    [pools]
  );

  const inProcess = useMemo(
    () => pools.filter(p => p.status === 'LOCKED' || p.status === 'IN_PROGRESS'),
    [pools]
  );

  const completed = useMemo(
    () => pools.filter(p => p.status === 'COMPLETED' || p.status === 'CANCELLED'),
    [pools]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pools...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md">
          <div className="text-red-600 text-center">
            <h3 className="text-lg font-semibold mb-2">Error Loading Pools</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadPools}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="px-3 sm:px-6 lg:px-8">
        <div className="mb-4 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">All Pools</h1>
          <p className="mt-1 md:mt-2 text-sm md:text-base text-gray-600">
            Join a pool and compete with other fans!
          </p>
        </div>

        {pools.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Pools Available
            </h3>
            <p className="text-gray-600">
              Check back soon for new fantasy pools!
            </p>
          </div>
        ) : (
          <div className="space-y-5 md:space-y-10">
            {/* My Pools */}
            {isAuthenticated && myPools.length > 0 && (
              <PoolSection
                title="My Pools"
                count={myPools.length}
                badgeColor="bg-indigo-100 text-indigo-700"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                  {myPools.map((pool) => (
                    // showEnteredBadge=false: the section header already conveys "this is mine"
                    <PoolCard key={pool.id} pool={pool} entered showEnteredBadge={false} />
                  ))}
                </div>
              </PoolSection>
            )}

            {/* Accepting Entries */}
            {acceptingEntries.length > 0 && (
              <PoolSection
                title="Accepting Entries"
                count={acceptingEntries.length}
                badgeColor="bg-green-100 text-green-800"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                  {acceptingEntries.map((pool) => (
                    <PoolCard key={pool.id} pool={pool} entered={myPoolIds.has(pool.id)} />
                  ))}
                </div>
              </PoolSection>
            )}

            {/* In Process */}
            {inProcess.length > 0 && (
              <PoolSection
                title="In Process"
                count={inProcess.length}
                badgeColor="bg-blue-100 text-blue-800"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                  {inProcess.map((pool) => (
                    <PoolCard key={pool.id} pool={pool} entered={myPoolIds.has(pool.id)} />
                  ))}
                </div>
              </PoolSection>
            )}

            {/* Completed — collapsed by default */}
            {completed.length > 0 && (
              <PoolSection
                title="Completed"
                count={completed.length}
                badgeColor="bg-gray-100 text-gray-800"
                defaultOpen={false}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                  {completed.map((pool) => (
                    <PoolCard key={pool.id} pool={pool} entered={myPoolIds.has(pool.id)} />
                  ))}
                </div>
              </PoolSection>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
